import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import OrbLoader from "../common/OrbLoader";
import {
  UserLoginEntry,
  useGetUserLoginsQuery,
} from "../../store/apis/usersApi";

/**
 * Historial de conexiones de un usuario en /users/:id. Hay una fila por día: la primera
 * petición autenticada de cada día (Europe/Madrid), también con la sesión ya abierta.
 */

const INITIAL_ROWS = 15;

// Navegador y sistema a partir del user agent, solo para reconocerlos de un vistazo
const describeDevice = (userAgent: string | null): string => {
  if (!userAgent) return "-";
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /OPR\/|Opera/.test(userAgent)
      ? "Opera"
      : /Chrome\//.test(userAgent)
        ? "Chrome"
        : /Firefox\//.test(userAgent)
          ? "Firefox"
          : /Safari\//.test(userAgent)
            ? "Safari"
            : null;
  const os = /Windows/.test(userAgent)
    ? "Windows"
    : /iPhone|iPad/.test(userAgent)
      ? "iOS"
      : /Android/.test(userAgent)
        ? "Android"
        : /Mac OS X|Macintosh/.test(userAgent)
          ? "macOS"
          : /Linux/.test(userAgent)
            ? "Linux"
            : null;
  const parts = [browser, os].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : userAgent.slice(0, 40);
};

interface Props {
  userId: string;
}

const UserLoginHistory: React.FC<Props> = ({ userId }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading, isError } = useGetUserLoginsQuery(userId, {
    skip: !userId,
  });
  const logins: UserLoginEntry[] = useMemo(() => data?.data ?? [], [data]);

  const last30Days = useMemo(() => {
    const since = dayjs().subtract(30, "day");
    return logins.filter((login) => dayjs(login.loggedAt).isAfter(since))
      .length;
  }, [logins]);

  const visible = showAll ? logins : logins.slice(0, INITIAL_ROWS);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <OrbLoader />
        </Box>
      );
    }
    if (isError) {
      return (
        <Typography variant="body1" color="error">
          {t("users.loginHistory.error")}
        </Typography>
      );
    }
    if (logins.length === 0) {
      return (
        <Typography variant="body1" color={theme.palette.text.secondary}>
          {t("users.loginHistory.empty")}
        </Typography>
      );
    }
    return (
      <>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("users.loginHistory.date")}</TableCell>
              <TableCell>{t("users.loginHistory.device")}</TableCell>
              <TableCell>{t("users.loginHistory.ip")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((login) => (
              <TableRow key={login.id}>
                <TableCell>
                  {dayjs(login.loggedAt).format("DD/MM/YYYY HH:mm")}
                </TableCell>
                <TableCell title={login.userAgent ?? undefined}>
                  {describeDevice(login.userAgent)}
                </TableCell>
                <TableCell>{login.ipAddress ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {logins.length > INITIAL_ROWS && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button onClick={() => setShowAll((value) => !value)}>
              {showAll
                ? t("users.loginHistory.showLess")
                : t("users.loginHistory.showAll", { count: logins.length })}
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Paper
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t("users.loginHistory.title")}
        </Typography>
        {logins.length > 0 && (
          <Chip
            size="small"
            label={t("users.loginHistory.last30Days", { count: last30Days })}
          />
        )}
      </Box>
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        sx={{ mb: 2 }}
      >
        {t("users.loginHistory.description")}
      </Typography>
      {renderBody()}
    </Paper>
  );
};

export default UserLoginHistory;
